/* eslint-disable @typescript-eslint/no-unused-vars */
import { DocumentValue, LiveQuery, Subscription } from '@dittolive/ditto'
import { useEffect, useRef, useState } from 'react'

import { Issue } from '../store/types'
import useCurrentApp from './useCurrentApp'
import useDittoClient from './useDittoClient'
import useStatus, { Status } from './useStatus'
import { stopLiveQuery } from './utils'

type Result = {
  /** Issue query status */
  status: Status
  /** Application issue */
  issue?: Issue
  /** Issue document ID. */
  id?: string
  /** Updates issue title */
  updateTitle: (title: string) => Promise<Issue>
  /** Updates issue description */
  updateDescription: (description: string) => Promise<Issue>
  /** Updates issue platform value */
  updatePlatform: (platform: string) => Promise<Issue>
  /** Updates issue SDK version */
  updateSdkVersion: (sdkVersion: string) => Promise<Issue>
  /** Adds a new comment */
  addComment: (
    body: string,
    options?: { shouldClose?: boolean; shouldReopen?: boolean },
  ) => Promise<Issue>
  /** Reopen issue */
  reopenIssue: () => Promise<Issue>
  /** Close issue */
  closeIssue: () => Promise<Issue>
}

/** Fetches a single issue from the store by its ID and observes changes on it. */
const useIssue = (appId: string): Result => {
  const client = useDittoClient()
  const { organizationId, issueId } = useCurrentApp(appId)
  const [issue, setIssue] = useState<Issue | undefined>(undefined)
  const issueSub = useRef<Subscription | undefined>()
  const issueQuery = useRef<LiveQuery | undefined>()
  const { state, dispatchError, dispatchPending, dispatchSuccess } = useStatus()

  useEffect(() => {
    if (issueId) {
      stopLiveQuery(issueQuery)
      dispatchPending()
      try {
        const op = client.store.collection('issues').findByID(issueId)
        issueSub.current = op.subscribe()
        issueQuery.current = op.observeLocal((document) => {
          if (document) {
            setIssue(document.value as Issue)
          }
        })
        dispatchSuccess()
      } catch (err) {
        dispatchError(err as Record<string, unknown>)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, appId, issueId])

  useEffect(() => {
    return () => {
      stopLiveQuery(issueQuery)
      issueSub.current?.cancel()
    }
  }, [])

  const toIssue = (document: DocumentValue) => document as unknown as Issue
  const updateState = (documentValue: DocumentValue) => {
    const updated: Issue = toIssue(documentValue)
    setIssue(updated)
    return updated
  }
  const updateTitle = (value: string): Promise<Issue> =>
    client.updateTitle(issueId!, value).then(updateState)

  const updateSdkVersion = (value: string): Promise<Issue> =>
    client.updateSdkVersion(issueId!, value).then(updateState)

  const updatePlatform = (value: string): Promise<Issue> =>
    client.updatePlatform(issueId!, value).then(updateState)

  const updateDescription = (value: string): Promise<Issue> =>
    client.updateDescription(issueId!, value).then(updateState)

  const reopenIssue = (): Promise<Issue> =>
    client.reopenIssue(issueId!).then(updateState)

  const closeIssue = (): Promise<Issue> =>
    client.closeIssue(issueId!).then(updateState)

  const addComment = (
    body: string,
    options?: { shouldClose?: boolean; shouldReopen?: boolean },
  ): Promise<Issue> =>
    client.addComment(issueId!, body, options).then(updateState)

  return {
    status: state.status,
    id: issue?.id,
    issue: issue as unknown as Issue,
    updateTitle,
    updateSdkVersion,
    updatePlatform,
    updateDescription,
    reopenIssue,
    addComment,
    closeIssue,
  }
}

export default useIssue
